import { CheckCircle2, XCircle, ChevronDown, Loader2 } from 'lucide-react';

export interface TldResult {
  tld: string;
  domain: string;
  available: boolean;
  status: string;
}

interface DomainResultsGridProps {
  query: string;
  results: TldResult[];
  extendedAvailable: boolean;
  onShowMore: () => void;
  onSelect: (domain: string) => void;
  selectedDomain: string | null;
  isLoadingMore: boolean;
}

export function DomainResultsGrid({
  query,
  results,
  extendedAvailable,
  onShowMore,
  onSelect,
  selectedDomain,
  isLoadingMore,
}: DomainResultsGridProps) {
  if (results.length === 0) {
    return null;
  }

  const availableResults = results.filter((r) => r.available);

  return (
    <div className="space-y-4">
      {/* Results header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          <span className="font-medium">{availableResults.length}</span> of{' '}
          <span className="font-medium">{results.length}</span> extensions available for{' '}
          <span className="font-semibold text-gray-900">{query}</span>
        </p>
      </div>

      {/* Results grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {results.map((result) => (
          <button
            key={result.domain}
            onClick={() => result.available && onSelect(result.domain)}
            disabled={!result.available}
            className={`
              relative flex items-center justify-between p-3 rounded-lg border-2 transition-all text-left
              ${
                result.available
                  ? selectedDomain === result.domain
                    ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                    : 'border-gray-200 bg-white hover:border-primary-300 hover:bg-gray-50 cursor-pointer'
                  : 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
              }
            `}
          >
            <div className="flex items-center gap-2 min-w-0">
              {result.available ? (
                <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
              ) : (
                <XCircle className="w-4 h-4 text-gray-400 flex-shrink-0" />
              )}
              <span
                className={`font-medium truncate ${
                  result.available ? 'text-gray-900' : 'text-gray-400'
                }`}
              >
                {result.tld}
              </span>
            </div>
            {result.available && (
              <span className="text-xs text-primary-600 font-medium ml-2 flex-shrink-0">
                Select
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Show more button */}
      {extendedAvailable && (
        <button
          onClick={onShowMore}
          disabled={isLoadingMore}
          className="w-full flex items-center justify-center gap-2 py-3 text-sm font-medium text-gray-600 hover:text-primary-600 hover:bg-gray-50 rounded-lg border border-dashed border-gray-300 transition-all"
        >
          {isLoadingMore ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading more options...
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4" />
              Show more domain extensions
            </>
          )}
        </button>
      )}
    </div>
  );
}
